import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Forzamos locale 'es-ES' en TODOS los precios para que el símbolo sea
// distintivo según la moneda real:
//   EUR → "€"
//   USD → "US$"   (no "$" genérico)
//   COP → "COP"   (con currencyDisplay: 'code' o "COL$" según runtime)
//   CLP → "CL$"
//   MXN → "MX$"
//   ARS → "AR$"
// Esto evita que todas las propiedades parezcan estar en USD por usar "$".
const NO_DECIMAL_CURRENCIES = new Set(['CLP', 'COP', 'JPY', 'KRW', 'UYU']);

export function formatPrice(
  price: number | string | null | undefined,
  currency: string = 'EUR',
): string {
  const value = typeof price === 'string' ? Number(price) : price;
  if (value === null || value === undefined || !Number.isFinite(value) || value <= 0) {
    return 'Consultar';
  }

  const cur = (currency || 'EUR').toUpperCase();
  const fractionDigits = NO_DECIMAL_CURRENCIES.has(cur) ? 0 : 0;

  try {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: cur,
      currencyDisplay: 'symbol',
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: 0,
    }).format(value as number);
  } catch {
    return `${(value as number).toLocaleString('es-ES')} ${cur}`;
  }
}

export function formatArea(area: number): string {
  return `${area} m²`;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

export function formatDate(date: string, locale: string = 'es'): string {
  const dateObj = new Date(date);
  
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  
  return dateObj.toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', options);
}

export function getReadingTime(text: string): number {
  const wordsPerMinute = 200;
  const words = text.split(/\s+/).length;
  return Math.ceil(words / wordsPerMinute);
}

export function generateWhatsAppLink(phone: string, message: string): string {
  const cleanPhone = phone.replace(/[^\d]/g, '');
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
}

export function generatePropertyShareLinks(property: any, baseUrl: string = '') {
  const url = `${baseUrl}/propiedad/${property.id}`;
  const title = property.title;
  const description = `${property.title} - ${formatPrice(property.price, property.currency)}`;
  
  return {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`,
    email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(description + ' ' + url)}`
  };
}